<template>
  <div class="product-grid-simple">
    <product-card
      v-for="(product, index) in products"
      :key="`${product.key}-${index}`"
      :grid-active-variant-selector="activeVariantSelector"
      :initial-product="product"
      :tracking-parameters="trackingParameters"
      :use-packshot="productCardUsePackshot"
      @variant-selector-toggled="handleVariantSelectorToggle"
    />
  </div>
</template>

<script>

/**
 * Component: Product Grid (simple)
 * ------------------------------------------------------------------------------
 * - Simple Product Card Grid.
 * @param {Array} products - Array of formatted product objects.
 * @param {Boolean} [productCardUsePackshot] - If to use Packshots as Primary Product Card images.
 * @param {Object} [trackingParameters] - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'

import { useWishlistStore } from '~stores/wishlist'

import ProductCard from '~components/product-card/product-card'

export default {
  name: 'ProductGridSimple',

  components: {
    ProductCard,
  },

  props: {
    products: {
      type: Array,
      default: () => [],
    },
    productCardUsePackshot: {
      type: Boolean,
      default: false,
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['variant-selector-toggled'],

  data() {
    return {
      activeVariantSelector: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Map Wishlist state.
     */
    ...mapState(useWishlistStore, ['wishlistReady']),
  },

  watch: {

    /**
     * Watch 'Product' data
     * - Initial prop passed through will be an array of 4 empty products
     * - This is to display the skeletons
     * - Check every product is a valid 'product' object.
     * - This assumes that once product data is set, it won't change.
     */
    products: {
      handler() {
        if (this.state === 'ready') {
          return
        }

        const ready = this.products.every((product) => {
          return typeof product.handle === 'string'
        })

        this.state = ready ? 'ready' : 'loading'
      },
      deep: true,
    },
  },

  methods: {

    /**
     * Updates the handle of the current open Variant Selector.
     * - This variable is updated from an event inside the Product Card, running
     * whenever a variant selector is toggled.
     * - The handle is passed down to Product Cards
     * - This ensures only one selector is open at a given time.
     * - Emits `variant-selector-toggled` event upward from an product-card
     * component.
     * - The event is re-emitted to allow parent components to respond to
     * overlay changes.
     * @param {Object} payload - Emitted payload object.
     * @param {String} payload.handle - The handle of the product.
     * @param {String} payload.state - State of the variant selector (`visible`/`hidden`).
     *
     * @event variant-selector-toggled
     */
    handleVariantSelectorToggle(payload) {
      const { handle = false, state = 'hidden' } = payload || {}

      if (state === 'visible') {
        this.activeVariantSelector = handle
      }

      this.$emit('variant-selector-toggled', payload)
    },
  },
}
</script>
